import React from "react";
import { motion } from "framer-motion";
import { Check, Copy, ArrowRight, Sparkles, MessageCircle } from "lucide-react";
import { motionOk } from "../lib/motionOk.js";
import { useLang } from "../i18n.jsx";

export default function PricingFooter() {
  const { content, copied, setCopied, waLink } = useLang();
  const { pricing, studio, footer } = content;
  const addOnLabel = content.pricing.addOnLabel;
  const copyEmail = function(){ try { window.navigator.clipboard.writeText(studio.email); } catch(e){ var ta=document.createElement("textarea"); ta.value=studio.email; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove(); } setCopied(true); setTimeout(function(){ setCopied(false); },2000); };
  return (
    <section id="paket" className="relative px-6 py-20 overflow-hidden">
      <div className="pointer-events-none absolute -top-10 right-0 w-[360px] h-[360px] rounded-full bg-acid/[0.04] blur-3xl" />
      <div className="relative mx-auto max-w-6xl">
        <motion.div
          initial={motionOk ? { opacity: 0, y: 26 } : false}
          whileInView={motionOk ? { opacity: 1, y: 0 } : undefined}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 grid gap-5 lg:grid-cols-3"
        >
          {pricing.plans.map(function (p, i) {
            return (
              <motion.div key={p.name} className={"relative rounded-3xl border p-8 flex flex-col " + (p.featured ? "bg-acid/5 border-acid/40" : "bg-ink2/50 border-paper/10")}>
                {p.featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-acid px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-ink">Best Seller</span>}
                <h3 className="font-display text-lg font-bold text-paper">{p.name}</h3>
                <p className="mt-1 text-xs uppercase tracking-widest text-paper/60">{p.tag}</p>
                <div className="mt-6 font-display text-4xl font-extrabold text-paper">{p.price}</div>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map(function (ft) { return (<li key={ft} className="flex items-center gap-3 text-[15px] text-paper/70"><Check className={"w-4 h-4 shrink-0 " + (p.featured ? "text-acid" : "text-paper/50")} /> {ft}</li>); })}
                </ul>
                <a href="#konsultasi" onClick={() => window.dispatchEvent(new CustomEvent("ap:select-plan", { detail: p.name }))} data-cursor className={"mt-8 flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold transition-colors " + (p.featured ? "bg-acid text-ink hover:bg-paper" : "border border-paper/25 text-paper hover:border-acid hover:text-acid")}>{p.cta} <ArrowRight className="w-4 h-4" /></a>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          initial={motionOk ? { opacity: 0, y: 24 } : false}
          whileInView={motionOk ? { opacity: 1, y: 0 } : undefined}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 rounded-3xl border border-paper/10 bg-ink2/40 p-6"
        >
          <p className="text-[11px] uppercase tracking-[0.25em] text-acid">{addOnLabel}</p>
          <div className="mt-3 flex flex-wrap gap-2.5">
            {pricing.addOns.map(function (a) { return (<span key={a} className="flex items-center gap-1.5 rounded-full border border-paper/15 px-4 py-2 text-sm text-paper/80"><Sparkles className="w-3.5 h-3.5 text-acid" /> {a}</span>); })}
          </div>
          <p className="mt-5 text-[15px] text-paper/60">{pricing.footnote}</p>
        </motion.div>
      </div>

      <footer className="relative mt-24 overflow-hidden px-6">
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[380px] bg-[radial-gradient(ellipse_at_bottom,rgba(0,0,0,0.09),transparent_65%)]" />
        <div className="relative mx-auto max-w-6xl text-center">
          <a href={waLink} target="_blank" rel="noopener noreferrer" data-cursor className="inline-flex items-center gap-2 rounded-full border border-acid/50 bg-acid/10 px-5 py-2.5 text-[13px] font-semibold text-acid hover:bg-acid hover:text-ink transition">
            <MessageCircle className="w-4 h-4" /> {footer.waCta}
          </a>
          <p className="mt-6 font-display text-[clamp(1.6rem,4.4vw,3.4rem)] font-extrabold tracking-tight leading-[1.05] text-paper">
            {footer.headlineA} <span className="inline-block ml-2 rounded-[0.12em] bg-acid px-[0.14em] py-[0.04em] text-ink">{footer.headlineB}</span>
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href={"mailto:" + studio.email} data-cursor className="rounded-full border border-paper/20 px-5 py-2.5 text-sm text-paper/80 hover:border-paper/50 hover:text-paper transition">{studio.email}</a>
            <button onClick={copyEmail} data-cursor className="flex items-center gap-2 rounded-full border border-paper/20 px-5 py-2.5 text-sm text-paper/80 hover:border-acid hover:text-acid transition">
              {copied ? footer.copied : footer.copyEmail} <Copy className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {studio.socials.map(function (s) { return (<a key={s.label} href={s.href} className="rounded-full border border-paper/12 px-4 py-2 text-sm text-paper/65 hover:border-paper/40 hover:text-paper transition">{s.label}</a>); })}
          </div>
          <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-paper/10 pt-8 text-xs uppercase tracking-widest text-paper/40">
            <span>&copy; 2026 {studio.name}. {footer.rights}</span>
            <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> {footer.tagline}</span>
          </div>
        </div>
      </footer>
    </section>
  );
}
